import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, setDoc, serverTimestamp, getDoc, doc, query, orderBy, limit } from 'firebase/firestore';
import { allocateTaskNumber } from '../utils/associationUtils';
import { db } from '../utils/firebase';
import { Link } from 'react-router-dom';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';
import TaskModal from '../components/forms/TaskModal';
import { useAuth } from '../components/auth/AuthContext'; 
import { formatDate } from '../utils/dateHelpers';
import { collectionPath } from '../utils/firestoreCollections';
import Toast from '../components/common/Toast';
import { generateTaskId } from '../utils/documentIdUtils';
import { logUserAction } from '../utils/logger';
import LeaderboardWidget from '../components/gamification/LeaderboardWidget';
import { canViewAllData, FEATURE_PERMISSIONS } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';
import { isSalesTeamMember } from '../utils/salesTeamFilter';

const USERS_PAGE_SIZE = 500; // Fetch enough users to reliably filter down to Sales Rep / Sales Manager titles

const SalesRepPage = () => {
    const { currentUser } = useAuth();
    const { can } = usePermissions(currentUser);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const fb = useFilterBar({ search: '', sortBy: 'lastName', sortDir: 'asc' });
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        if (!currentUser) return;
        setLoading(true);
        const unsub = onSnapshot(
            query(
                collection(db, collectionPath('users')),
                orderBy('lastName', 'asc'),
                limit(USERS_PAGE_SIZE)
            ),
            (snap) => {
                setUsers(snap.docs
                    .map(doc => ({ uid: doc.id, ...doc.data() }))
                    .filter(u => u.role !== 'disabled')
                    // Only show users whose title is Sales Rep or Sales Manager (case-insensitive)
                    .filter(isSalesTeamMember)
                );
                setLoading(false);
                
            }
        );
        return unsub;
    }, [currentUser]);

    const handleSort = (column) => {
        if (fb.draft.sortBy === column) {
            fb.setDraft('sortDir', fb.draft.sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            fb.setDraft('sortBy', column);
            fb.setDraft('sortDir', 'asc');
        }
    };

    
    // Helper to determine if address info should be shown for a user
    const canShowAddress = (u) =>
        canViewAllData(currentUser) || currentUser?.uid === u.uid;

    // Add all visible fields to search (memoized)
    const filteredUsers = useMemo(() => {
        const q = (fb.applied.search || '').toLowerCase().trim();
        if (!q) return users;

        return users.filter(u => {
            const name = (u.name || `${u.firstName || ''} ${u.lastName || ''}` || '').toLowerCase();
            const email = (u.email || '').toLowerCase();
            const phone = (u.phone || '').toLowerCase();

            const extraFields = [
                u.managerName, u.title, u.department, u.state,
                Array.isArray(u.territories) ? u.territories.join(' ') : ''
            ].map(x => (x || '').toLowerCase()).join(' ');

            return [name, email, phone, extraFields].some(s => s.includes(q));
        });
    }, [users, fb.applied.search, currentUser]);

    const sortedUsers = useMemo(() => {
        const sorted = [...filteredUsers];
        
        if (fb.applied.sortBy) {
            sorted.sort((a, b) => {
                let aValue, bValue;
                if (fb.applied.sortBy === 'full_name' || fb.applied.sortBy === 'name') {
                    // Match the display logic: u.full_name || u.displayName
                    aValue = (a.full_name || a.displayName || '').toString().trim();
                    bValue = (b.full_name || b.displayName || '').toString().trim();

                } else {
                    aValue = (a[fb.applied.sortBy] ?? '');
                    bValue = (b[fb.applied.sortBy] ?? '');
                }
                if (typeof aValue === 'string' || typeof bValue === 'string') {
                    aValue = (aValue || '').toString();
                    bValue = (bValue || '').toString();
                    return fb.applied.sortDir === 'asc'
                        ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
                        : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
                }
                return fb.applied.sortDir === 'asc'
                    ? (aValue ?? 0) - (bValue ?? 0)
                    : (bValue ?? 0) - (aValue ?? 0);
            });
        }
        return sorted;
    }, [filteredUsers, fb.applied.sortBy, fb.applied.sortDir]);

    const handleAssignTask = async (task) => {
        const seq = await allocateTaskNumber();

        // Generate task ID using TSK-# format (matches TaskPage.jsx)
        const taskDocId = generateTaskId(seq);
        const taskNumberLabel = taskDocId; // Use same format for label

        await setDoc(doc(db, collectionPath('tasks'), taskDocId), {
            ...task,
            taskNumber: seq,
            taskNumberLabel,
            status: 'Open',
            notes: '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        
        // Log task creation
        await logUserAction({
            action: 'Task Created',
            details: `Task ${taskNumberLabel} created from Sales Reps page: "${task.title || task.subject || ''}"`,
            entityType: 'Task',
            entityId: taskDocId,
        });
        
        setShowTaskModal(false);
        setToast({ message: `Task ${taskNumberLabel} created successfully!`, type: 'success' });
    };

    // Helper to determine if name should be a link
    // Requires Sales Reps page permission or viewing own profile
    const canLinkName = (u) =>
        can(FEATURE_PERMISSIONS.SALES_REP_VIEW_ALL) || currentUser?.uid === u.uid;

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Sales Representatives</h2>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-4 space-y-2 md:space-y-0">
                <div className="flex items-center w-full md:w-auto">
                    <span className="mr-2 font-medium text-gray-700">Search:</span>
                    <input
                        type="text"
                        placeholder="Search by name, email, etc..."
                        className={`border rounded px-3 py-2 w-full md:w-[24rem] transition-colors duration-150 ${
                            fb.draft.search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                        }`}
                        value={fb.draft.search}
                        onChange={e => fb.setDraft('search', e.target.value)}
                        onKeyDown={fb.applyOnEnter}
                    />
                    {can(FEATURE_PERMISSIONS.TASK_CREATE) && (
                        <button
                            className="ml-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500"
                            onClick={() => setShowTaskModal(true)}
                        >
                            Create Task
                        </button>
                    )}
                </div>
                <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
            </div>
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="w-full whitespace-no-wrap">
                    <thead>
                        <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('full_name')}>
                                Full Name {fb.draft.sortBy === 'full_name' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('displayName')}>
                                Display Name {fb.draft.sortBy === 'displayName' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('email')}>
                                Email {fb.draft.sortBy === 'email' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('phone')}>
                                Phone {fb.draft.sortBy === 'phone' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('state')}>
                                State {fb.draft.sortBy === 'state' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                            </th>
                            {/* Territories column: visible to all viewers */}
                            <th className="px-4 py-3">
                                Territories
                            </th>

                            {/* Show manager/title/department columns for admins */}
                            {canViewAllData(currentUser) && (
                                <>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('managerName')}>
                                        Assigned Manager {fb.draft.sortBy === 'managerName' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('title')}>
                                        Title {fb.draft.sortBy === 'title' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('department')}>
                                        Department {fb.draft.sortBy === 'department' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                </>
                            )}
                            {/* Role column: admin only */}
                            {currentUser?.role === 'admin' && (
                                <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('role')}>
                                    Role {fb.draft.sortBy === 'role' && (fb.draft.sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y">
                        {loading && (
                            <tr>
                                <td colSpan={currentUser?.role === 'admin' ? 10 : canViewAllData(currentUser) ? 9 : 6} className="text-center py-4">Loading users...</td>
                            </tr>
                        )}
                        {!loading && sortedUsers.length === 0 && (
                            <tr>
                                <td colSpan={currentUser?.role === 'admin' ? 10 : canViewAllData(currentUser) ? 9 : 6} className="text-center py-4">No users found.</td>
                            </tr>
                        )}
                        {!loading && sortedUsers.map(u => (
                            <tr key={u.uid} className="text-gray-700 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium">
                                    {canLinkName(u) ? (
                                        <Link
                                            to={`/sales-reps/${u.uid}`}
                                            className="text-indigo-600 hover:underline"
                                        >
                                            {u.full_name || u.displayName || '-'}
                                        </Link>
                                    ) : (
                                        <span>
                                            {u.full_name || u.displayName || '-'}
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-sm">{u.displayName || '-'}</td>
                                <td className="px-4 py-3 text-sm">{u.email}</td>
                                <td className="px-4 py-3 text-sm">{u.phone || '-'}</td>
                                <td className="px-4 py-3 text-sm">{u.state || '-'}</td>
                                {/* Territories column: visible to all viewers */}
                                <td className="px-4 py-3 text-sm">
                                    {Array.isArray(u.territories) && u.territories.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {u.territories.map(t => (
                                                <span
                                                    key={t}
                                                    className="inline-flex items-center bg-indigo-50 text-indigo-800 border border-indigo-200 text-xs rounded-full px-2 py-0.5"
                                                >
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    ) : '-'}
                                </td>

                                {/* Manager/title/department columns: admin only */}
                                {canViewAllData(currentUser) && (
                                    <>
                                        <td className="px-4 py-3 text-sm">{u.managerName || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{u.title || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{u.department || '-'}</td>
                                    </>
                                )}
                                {/* Role column: admin only */}
                                {currentUser?.role === 'admin' && (
                                    <td className="px-4 py-3 text-sm">{u.role ? u.role.charAt(0).toUpperCase() + u.role.slice(1) : '-'}</td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {/* Team Leaderboard Section */}
            <div className="mt-8">
                <LeaderboardWidget maxEntries={15} showRewardButton={can(FEATURE_PERMISSIONS.TASK_VIEW_ALL)} />
            </div>
            
            {showTaskModal && can(FEATURE_PERMISSIONS.TASK_CREATE) && (
                <TaskModal
                    salesReps={users.filter(u => u.userId || u.uid)}
                    onSave={handleAssignTask}
                    onClose={() => setShowTaskModal(false)}
                />
            )}
        </main>
    );
};

export default SalesRepPage;